import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../services/api";
import { formatEnglishDate } from "../../utils/dateFormatting";
import LevelBadge from "../certification/LevelBadge";

/**
 * Componente per visualizzare la lista delle richieste ESG (Admin)
 * @param {object} props
 * @param {string} props.adminKey - Chiave admin per autenticazione
 * @param {string} props.walletAddress - Indirizzo wallet dell'admin
 */
const AdminESGList = ({ adminKey, walletAddress }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    q: "",
    page: 1,
    pageSize: 20,
  });
  const [total, setTotal] = useState(0);

  // Carica le richieste
  const loadRequests = async () => {
    if (!adminKey) {
      setError("Admin key required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await adminApi.getRequests(filters, adminKey, walletAddress);
      setRequests(result.requests || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error loading requests:", err);
      setError(err.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [filters, adminKey]);

  // Gestisce il cambio di filtro
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset alla prima pagina
    }));
  };

  // Gestisce il cambio pagina
  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  // Naviga al dettaglio
  const handleViewDetail = (requestId) => {
    navigate(`/admin/esg/requests/${requestId}`);
  };

  // Elimina una richiesta
  const handleDeleteRequest = async (requestId, companyName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la richiesta di ${companyName}? Questa azione è irreversibile.`)) {
      return;
    }

    try {
      await adminApi.deleteRequest(requestId, adminKey, walletAddress);
      setActionMessage("✓ Richiesta eliminata con successo");
      setTimeout(() => setActionMessage(""), 3000);
      await loadRequests(); // Ricarica la lista
    } catch (err) {
      console.error("Error deleting request:", err);
      window.alert(`Errore durante l'eliminazione: ${err.message}`);
    }
  };

  // Aggiorna lo stato di una richiesta
  const handleUpdateStatus = async (requestId, newStatus, companyName) => {
    const notes = window.prompt(`Aggiungi note per il cambio di stato di ${companyName} a ${newStatus.toUpperCase()}:`);
    if (notes === null) return; // Annullato

    try {
      await adminApi.updateRequestStatus(requestId, newStatus, notes || "", adminKey, walletAddress);
      setActionMessage(`✓ Status aggiornato a ${newStatus.toUpperCase()}`);
      setTimeout(() => setActionMessage(""), 3000);
      await loadRequests(); // Ricarica la lista
    } catch (err) {
      console.error("Error updating status:", err);
      window.alert(`Errore durante l'aggiornamento: ${err.message}`);
    }
  };

  const [actionMessage, setActionMessage] = useState("");

  const getCertificationMeta = (request) => {
    const certification = request?.certification || request?.publicCertification || {};
    const level = certification.level || request?.certificationLevel || request?.level || "NONE";
    const badges = certification.badges || request?.badges || [];
    const badgeCount = Array.isArray(badges) ? badges.length : request?.badgesCount || 0;
    const ipfsJsonCid = certification.ipfsJsonCid || request?.ipfsJsonCid;
    const ipfsPdfCid = certification.ipfsPdfCid || request?.ipfsPdfCid;
    const isPublished = Boolean(ipfsJsonCid || ipfsPdfCid);
    return { level, badgeCount, isPublished };
  };

  const getScoreValue = (request, key) => {
    const scores = request?.scores || request?.score || request?.scoringTotals || {};
    const lowerKey = key?.toLowerCase();
    return scores[key] ?? scores[lowerKey] ?? request?.[`score${key}`] ?? request?.[`score${key?.charAt(0).toUpperCase()}${key?.slice(1)}`] ?? "-";
  };

  // Ottiene il colore dello status
  const getStatusColor = (status) => {
    const colors = {
      pending: "#f39c12",    // Arancione (più visibile)
      approved: "#27ae60",   // Verde
      rejected: "#e74c3c",   // Rosso
      need_info: "#3498db",  // Blu
    };
    return colors[status?.toLowerCase()] || "#95a5a6";
  };

  // Ottiene il colore del testo per lo status
  const getStatusStyle = (status) => {
    const statusLower = status?.toLowerCase();
    return {
      backgroundColor: getStatusColor(statusLower),
      color: "white",
      padding: "6px 16px",
      borderRadius: "16px",
      fontSize: "12px",
      fontWeight: "700",
      textTransform: "uppercase",
      display: "inline-block",
      letterSpacing: "0.5px",
    };
  };

  // Calcola il numero di pagine
  const totalPages = Math.ceil(total / filters.pageSize);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>ESG Requests</h2>
        <button onClick={loadRequests} style={styles.refreshButton}>
          ↻ Refresh
        </button>
      </div>

      {/* Messaggio di azione */}
      {actionMessage && (
        <div style={styles.actionMessage}>
          {actionMessage}
        </div>
      )}

      {/* Filtri */}
      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="need_info">Need Info</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Search:</label>
          <input
            type="text"
            value={filters.q}
            onChange={(e) => handleFilterChange("q", e.target.value)}
            placeholder="Company name, email..."
            style={styles.filterInput}
          />
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Per page:</label>
          <select
            value={filters.pageSize}
            onChange={(e) =>
              handleFilterChange("pageSize", parseInt(e.target.value))
            }
            style={styles.filterSelect}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {/* Totale */}
      <div style={styles.totalBar}>
        Total: {total} request{total !== 1 ? "s" : ""}
      </div>

      {/* Stato di caricamento */}
      {loading && <div style={styles.loading}>Loading...</div>}

      {/* Errore */}
      {error && <div style={styles.error}>Error: {error}</div>}

      {/* Tabella richieste */}
      {!loading && !error && (
        <>
          {requests.length === 0 ? (
            <div style={styles.noResults}>No requests found</div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Company</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Wallet</th>
                    <th style={styles.th}>Livello</th>
                    <th style={styles.th}>Badge</th>
                    <th style={styles.th}>IPFS</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Score Compliance</th>
                    <th style={styles.th}>Score Sustainability</th>
                    <th style={styles.th}>Score Social</th>
                    <th style={styles.th}>Score Governance</th>
                    <th style={styles.th}>Attachments</th>
                    <th style={styles.th}>Submitted</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => {
                    const { level, badgeCount, isPublished } = getCertificationMeta(request);
                    return (
                    <tr key={request.id} style={styles.tr}>
                      <td style={styles.td}>
                        {request.id?.substring(0, 8) || "-"}
                      </td>
                      <td style={styles.td}>{request.companyName || "-"}</td>
                      <td style={styles.td}>{request.email || "-"}</td>
                      <td style={styles.td}>
                        <span 
                          style={{fontSize: '11px', fontFamily: 'monospace', wordBreak: 'break-all'}}
                          title={request.walletAddress || 'No wallet'}
                        >
                          {request.walletAddress || "-"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <LevelBadge level={level} size="small" showLabel={false} />
                      </td>
                      <td style={styles.td}>{badgeCount} badge</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            color: isPublished ? "#16a34a" : "#9ca3af",
                            fontWeight: 600,
                          }}
                        >
                          ● {isPublished ? "Pubblicato" : "Non pubblicato"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={getStatusStyle(request.status)}>
                          {request.status || "pending"}
                        </span>
                      </td>
                      <td style={styles.td}>{getScoreValue(request, "Compliance")}</td>
                      <td style={styles.td}>{getScoreValue(request, "Sustainability")}</td>
                      <td style={styles.td}>{getScoreValue(request, "Social")}</td>
                      <td style={styles.td}>{getScoreValue(request, "Governance")}</td>
                      <td style={styles.td}>
                        {request.attachmentsCount || 0}
                      </td>
                      <td style={styles.td}>
                        {formatEnglishDate(request.createdAt)}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          <button
                            onClick={() => handleViewDetail(request.id)}
                            style={styles.viewButton}
                            title="Visualizza dettagli"
                          >
                            View
                          </button>
                          
                          {/* Dropdown per cambiare status */}
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                handleUpdateStatus(request.id, e.target.value, request.companyName);
                                e.target.value = ""; // Reset
                              }
                            }}
                            style={styles.statusSelect}
                            title="Cambia stato"
                          >
                            <option value="">Change Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="need_info">Need Info</option>
                          </select>

                          <button
                            onClick={() => handleDeleteRequest(request.id, request.companyName)}
                            style={styles.deleteButton}
                            title="Elimina richiesta"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginazione */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page === 1}
                style={{
                  ...styles.pageButton,
                  opacity: filters.page === 1 ? 0.5 : 1,
                }}
              >
                Previous
              </button>

              <span style={styles.pageInfo}>
                Page {filters.page} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page >= totalPages}
                style={{
                  ...styles.pageButton,
                  opacity: filters.page >= totalPages ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#2c3e50",
  },
  refreshButton: {
    padding: "8px 16px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  filtersContainer: {
    display: "flex",
    gap: "20px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  filterLabel: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#2c3e50",
  },
  filterSelect: {
    padding: "8px",
    border: "1px solid #bdc3c7",
    borderRadius: "4px",
    fontSize: "14px",
  },
  filterInput: {
    padding: "8px",
    border: "1px solid #bdc3c7",
    borderRadius: "4px",
    fontSize: "14px",
    minWidth: "200px",
  },
  totalBar: {
    padding: "10px",
    backgroundColor: "#ecf0f1",
    borderRadius: "4px",
    marginBottom: "20px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#2c3e50",
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    color: "#7f8c8d",
    fontSize: "16px",
  },
  error: {
    padding: "20px",
    backgroundColor: "#f8d7da",
    color: "#721c24",
    borderRadius: "4px",
    border: "1px solid #f5c6cb",
  },
  noResults: {
    textAlign: "center",
    padding: "40px",
    color: "#7f8c8d",
    fontSize: "16px",
  },
  tableContainer: {
    overflowX: "auto",
    backgroundColor: "white",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    padding: "12px",
    textAlign: "left",
    backgroundColor: "#34495e",
    color: "white",
    fontWeight: "600",
    fontSize: "14px",
  },
  tr: {
    borderBottom: "1px solid #ecf0f1",
  },
  td: {
    padding: "12px",
    fontSize: "14px",
    color: "#2c3e50",
  },
  statusBadge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "12px",
    color: "white",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  viewButton: {
    padding: "6px 12px",
    backgroundColor: "#27ae60",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    whiteSpace: "nowrap",
  },
  statusSelect: {
    padding: "6px 8px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
  },
  deleteButton: {
    padding: "6px 10px",
    backgroundColor: "#e74c3c",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    lineHeight: "1",
  },
  actionMessage: {
    padding: "12px 20px",
    backgroundColor: "#d4edda",
    color: "#155724",
    borderRadius: "4px",
    marginBottom: "20px",
    border: "1px solid #c3e6cb",
    fontSize: "14px",
    fontWeight: "500",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginTop: "20px",
    padding: "20px",
  },
  pageButton: {
    padding: "8px 16px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  pageInfo: {
    fontSize: "14px",
    color: "#2c3e50",
    fontWeight: "500",
  },
};

export default AdminESGList;
